/**
 * Shared password validation utilities
 * Used by both client and server to ensure consistent password requirements
 */

export const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  maxLength: 128,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecialChar: true,
} as const

export interface PasswordValidationResult {
  valid: boolean
  errors: string[]
}

/**
 * Validate a password against all requirements
 * Returns an object with validation status and detailed error messages
 */
export function validatePassword(password: string): PasswordValidationResult {
  const errors: string[] = []

  if (!password) {
    return {
      valid: false,
      errors: [
        'Password is required, should be at least 8 characters long, include at least one uppercase letter, one lowercase letter, one number, and one special character.',
      ],
    }
  }

  if (password.length < PASSWORD_REQUIREMENTS.minLength) {
    errors.push(
      `Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters`
    )
  }

  if (password.length > PASSWORD_REQUIREMENTS.maxLength) {
    errors.push(
      `Password must be less than ${PASSWORD_REQUIREMENTS.maxLength} characters`
    )
  }

  if (PASSWORD_REQUIREMENTS.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter')
  }

  if (PASSWORD_REQUIREMENTS.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter')
  }

  if (PASSWORD_REQUIREMENTS.requireNumber && !/\d/.test(password)) {
    errors.push('Password must contain at least one number')
  }

  if (
    PASSWORD_REQUIREMENTS.requireSpecialChar &&
    !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
  ) {
    errors.push('Password must contain at least one special character')
  }

  return {
    valid: errors.length === 0,
    errors,
  }
}

/**
 * Get a human-readable list of password requirements
 */
export function getPasswordRequirements(): string[] {
  const requirements: string[] = []

  requirements.push(
    `At least ${PASSWORD_REQUIREMENTS.minLength} characters long`
  )

  if (PASSWORD_REQUIREMENTS.requireUppercase) {
    requirements.push('At least one uppercase letter (A-Z)')
  }

  if (PASSWORD_REQUIREMENTS.requireLowercase) {
    requirements.push('At least one lowercase letter (a-z)')
  }

  if (PASSWORD_REQUIREMENTS.requireNumber) {
    requirements.push('At least one number (0-9)')
  }

  if (PASSWORD_REQUIREMENTS.requireSpecialChar) {
    requirements.push('At least one special character (!@#$%^&* etc.)')
  }

  return requirements
}

/**
 * Get a single error message summarizing all password requirements
 * Useful for form validation error messages
 */
export function getPasswordErrorMessage(password: string): string | null {
  const result = validatePassword(password)
  if (result.valid) {
    return null
  }
  return result.errors.join('. \n')
}
